import { Injectable } from '@angular/core';
import { OrderingOption } from 'generated/graphql-types';

@Injectable({ providedIn: 'root' })
export class QueueHistoryRegisterSessionStorageService {
   private readonly queueHistoryOrderingOptionsStorageKey = '__ndt-queueHistory-ordering-options';
   private readonly activeCallsOrderingOptionsStorageKey = '__ndt-activeCalls-ordering-options';

   private readonly selectedQueueExtensionIdsStorageKey = '__ndt-queueHistory-selected-queue-extension-ids';

   private readonly queueHistoryToggleStateStorageKey = '__ndt-queueHistory-toggle-state';

   clearAll(): void {
      [
         this.queueHistoryOrderingOptionsStorageKey,
         this.activeCallsOrderingOptionsStorageKey,
         this.selectedQueueExtensionIdsStorageKey,
         this.queueHistoryToggleStateStorageKey,
      ].forEach((key) => localStorage.removeItem(key));
   }

   getOrderingOptions(scope: QueueHistoryRegisterStorageScope): OrderingOption[] | null {
      return this.getStoredValue<OrderingOption[]>(this.getOrderingOptionsStorageKey(scope));
   }

   setOrderingOptions(scope: QueueHistoryRegisterStorageScope, orderingOptions: OrderingOption[] | null): void {
      const options =
         scope === QueueHistoryRegisterStorageScope.ActiveCalls
            ? orderingOptions?.filter((option) => option.field !== 'enqueuedDateTime')
            : orderingOptions;

      localStorage.setItem(this.getOrderingOptionsStorageKey(scope), JSON.stringify(options));
   }

   getSelectedQueueExtensionIds(): number[] | null {
      return this.getStoredValue<number[]>(this.selectedQueueExtensionIdsStorageKey);
   }

   setSelectedQueueExtensionIds(queueExtensionIds: number[] | null): void {
      localStorage.setItem(this.selectedQueueExtensionIdsStorageKey, JSON.stringify(queueExtensionIds));
   }

   getQueueHistoryToggleState(): QueueHistoryToggleState | null {
      const storedToggleState = this.getStoredValue<Partial<QueueHistoryToggleState>>(
         this.queueHistoryToggleStateStorageKey
      );

      if (!storedToggleState) {
         return null;
      }

      return {
         groupMembersByQueue: storedToggleState.groupMembersByQueue ?? false,
         agentsShowLoggedOutAgents: storedToggleState.agentsShowLoggedOutAgents ?? false,
         queuesShowLoggedOutAgents: storedToggleState.queuesShowLoggedOutAgents ?? false,
         queuesWrapSwimlanes: storedToggleState.queuesWrapSwimlanes ?? true,
         queueCallsIncludeActiveCalls: storedToggleState.queueCallsIncludeActiveCalls ?? false,
         queueCallsShowEmptyQueues: storedToggleState.queueCallsShowEmptyQueues ?? false,
         queueCallsWrapSwimlanes: storedToggleState.queueCallsWrapSwimlanes ?? true,
      };
   }

   setQueueHistoryToggleState(toggleState: QueueHistoryToggleState | null): void {
      localStorage.setItem(this.queueHistoryToggleStateStorageKey, JSON.stringify(toggleState));
   }

   private getOrderingOptionsStorageKey(scope: QueueHistoryRegisterStorageScope): string {
      return scope === QueueHistoryRegisterStorageScope.ActiveCalls
         ? this.activeCallsOrderingOptionsStorageKey
         : this.queueHistoryOrderingOptionsStorageKey;
   }

   private getStoredValue<T>(storageKey: string): T | null {
      const localData = localStorage.getItem(storageKey);
      return localData ? JSON.parse(localData) : null;
   }
}

export enum QueueHistoryRegisterStorageScope {
   QueueHistory = 'queueHistory',
   ActiveCalls = 'activeCalls',
}

export interface QueueHistoryToggleState {
   groupMembersByQueue: boolean;
   agentsShowLoggedOutAgents: boolean;
   queuesShowLoggedOutAgents: boolean;
   queuesWrapSwimlanes: boolean;
   queueCallsIncludeActiveCalls: boolean;
   queueCallsShowEmptyQueues: boolean;
   queueCallsWrapSwimlanes: boolean;
}
